import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ViewMode } from "../components/molecules/ViewModeToggle/ViewModeToggle.types";

export interface KgHeaderBridge {
  headerMode?: "search" | "actions";
  search?: string;
  setSearch?: (value: string) => void;
  viewMode?: ViewMode;
  setViewMode?: (mode: ViewMode) => void;
  searchPlaceholder?: string;
  /** Places wide global-page searches in the center header column. */
  searchPlacement?: "center" | "right";
  showViewModeToggle?: boolean;
  onSaveDraft?: () => void;
  onSaveAndVerify?: () => void;
  isSaveDraftLoading?: boolean;
  isSaveVerifyLoading?: boolean;
  /**
   * Optional primary CTA rendered to the right of the search field.
   * Used by `/workspaces` for the "+ Add workspace" button. Reserved for one
   * action per page — keep the header chrome simple.
   */
  primaryAction?: {
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
  };
}

type KgHeaderReadContext = { bridge: KgHeaderBridge | null };
type KgHeaderWriteContext = (next: KgHeaderBridge | null) => void;

const KgHeaderContext = createContext<KgHeaderReadContext | null>(null);
// Separate write context — pages that only write don't subscribe to bridge changes
const KgHeaderSetContext = createContext<KgHeaderWriteContext | null>(null);

export const KgHeaderProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [bridge, setBridgeState] = useState<KgHeaderBridge | null>(null);
  const setBridge = useCallback((next: KgHeaderBridge | null) => {
    setBridgeState(next);
  }, []);
  const readValue = useMemo(() => ({ bridge }), [bridge]);
  return (
    <KgHeaderSetContext.Provider value={setBridge}>
      <KgHeaderContext.Provider value={readValue}>
        {children}
      </KgHeaderContext.Provider>
    </KgHeaderSetContext.Provider>
  );
};

export function useKgHeaderBridge(): KgHeaderBridge | null {
  const ctx = useContext(KgHeaderContext);
  return ctx?.bridge ?? null;
}

/** Publishes KG header chrome while a KG section page is mounted. */
export function useRegisterKgHeader(bridge: KgHeaderBridge): void {
  const setBridge = useContext(KgHeaderSetContext);
  useEffect(() => {
    if (!setBridge) return;
    setBridge(bridge);
    return () => {
      setBridge(null);
    };
  }, [bridge, setBridge]);
}

/** Canvas/catalog page — search + view mode toggle. */
export function useRegisterKgCanvasHeader(
  search: string,
  setSearch: (value: string) => void,
  viewMode: ViewMode,
  setViewMode: (mode: ViewMode) => void,
): void {
  const bridge = useMemo<KgHeaderBridge>(
    () => ({
      headerMode: "search",
      search,
      setSearch,
      viewMode,
      setViewMode,
      searchPlaceholder: "Search nodes",
      showViewModeToggle: true,
    }),
    [search, setSearch, viewMode, setViewMode],
  );
  useRegisterKgHeader(bridge);
}

export type KgAddDataModelHeaderHandlers = {
  onSaveDraft?: () => void;
  onSaveAndVerify?: () => void;
  isSaveDraftLoading?: boolean;
  isSaveVerifyLoading?: boolean;
};

/** Add data model page — Save as draft / Save and Verify (no search). */
export function useRegisterKgAddDataModelHeader({
  onSaveDraft,
  onSaveAndVerify,
  isSaveDraftLoading,
  isSaveVerifyLoading,
}: KgAddDataModelHeaderHandlers): void {
  const bridge = useMemo<KgHeaderBridge>(
    () => ({
      headerMode: "actions",
      onSaveDraft,
      onSaveAndVerify,
      isSaveDraftLoading,
      isSaveVerifyLoading,
    }),
    [onSaveDraft, onSaveAndVerify, isSaveDraftLoading, isSaveVerifyLoading],
  );
  useRegisterKgHeader(bridge);
}
